import React, {
  ReactNode,
  useState,
  useRef,
  useCallback,
  useLayoutEffect,
} from "react"
import { createPortal } from "react-dom"
import {
  computePosition,
  offset,
  flip,
  shift,
  autoUpdate,
} from "@floating-ui/dom"

const TOOLTIP_Z_INDEX = 10200

export interface TooltipProps {
  content: ReactNode
  children: ReactNode
  onClose?: () => void
}

const Tooltip: React.FC<TooltipProps> = ({ children, content, onClose }) => {
  const [open, setOpen] = useState(false)
  const referenceRef = useRef<HTMLButtonElement>(null)
  const floatingRef = useRef<HTMLDivElement>(null)

  const close = useCallback(() => {
    setOpen(false)
    onClose?.()
  }, [onClose])

  const toggle = useCallback(
    (e: React.SyntheticEvent) => {
      e.preventDefault()
      e.stopPropagation()
      setOpen((prev) => {
        const next = !prev
        if (!next) onClose?.()
        return next
      })
    },
    [onClose],
  )

  useLayoutEffect(() => {
    if (!open) return undefined

    const reference = referenceRef.current
    const floating = floatingRef.current
    if (!reference || !floating) return undefined

    const update = () => {
      computePosition(reference, floating, {
        placement: "top",
        strategy: "fixed",
        middleware: [offset(8), flip({ padding: 12 }), shift({ padding: 12 })],
      }).then(({ x, y }) => {
        Object.assign(floating.style, {
          position: "fixed",
          left: `${x}px`,
          top: `${y}px`,
        })
      })
    }

    update()
    const stopAutoUpdate = autoUpdate(reference, floating, update)

    const onDocPointerDown = (event: PointerEvent) => {
      const target = event.target as Node
      if (reference.contains(target) || floating.contains(target)) return
      close()
    }

    document.addEventListener("pointerdown", onDocPointerDown, true)

    return () => {
      stopAutoUpdate()
      document.removeEventListener("pointerdown", onDocPointerDown, true)
    }
  }, [open, close])

  return (
    <>
      <button
        ref={referenceRef}
        type="button"
        aria-label="查看说明"
        aria-expanded={open}
        onPointerUp={toggle}
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: 24,
          height: 24,
          padding: 0,
          margin: 0,
          border: "none",
          background: "transparent",
          cursor: "pointer",
          flexShrink: 0,
          position: "relative",
          zIndex: 2,
          verticalAlign: "middle",
          touchAction: "manipulation",
        }}
      >
        {children}
      </button>
      {open && typeof document !== "undefined"
        ? createPortal(
            <div
              ref={floatingRef}
              role="tooltip"
              style={{
                zIndex: TOOLTIP_Z_INDEX,
                maxWidth: "min(280px, calc(100vw - 24px))",
                padding: "8px 12px",
                borderRadius: 10,
                background: "rgba(0, 0, 0, 0.88)",
                color: "#fff",
                fontSize: 12,
                lineHeight: 1.45,
                wordBreak: "break-word",
                boxShadow: "0 4px 16px rgba(0, 0, 0, 0.18)",
              }}
              onPointerDown={(e) => e.stopPropagation()}
            >
              {content}
            </div>,
            document.body,
          )
        : null}
    </>
  )
}

export default Tooltip
